/**
 * Dashboard Help Content
 * 
 * Feature: 136-dashboard-alignment (T044)
 * Pages: /dashboard (all 4 role variants)
 * Constitution: v1.12.0
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const dashboardHelp: PageHelpContent = {
  pageTitle: 'Dashboard',
  purpose: 'Your role-based landing page showing live system health, metrics, and recent activity from the Fabric Control Plane.',
  sections: [
    {
      title: 'Role-Based Views',
      paragraphs: [
        'The dashboard adapts to your assigned role. Admins see full system health, alerts, and capacity metrics. Operators see active jobs, running backups, and system status. Managers see business metrics and client breakdowns. Users see favorites, notifications, and recent activity.',
      ],
      concepts: [
        { term: 'Admin Dashboard', definition: 'Full system overview with capacity health, alert counts, and recent activity. Includes SystemHealthWidget and ActiveAlertsWidget.' },
        { term: 'Operator Dashboard', definition: 'Operational view with running jobs, capacity utilization, and active alerts. Focused on day-to-day operations.' },
        { term: 'Manager Dashboard', definition: 'Business metrics including SLA compliance, backup coverage, and client breakdowns. Cost analytics coming soon.' },
        { term: 'User Dashboard', definition: 'Personal workspace with favorites, notifications, recent activity, and quick action links.' },
      ],
    },
    {
      title: 'Capacity Type Filter',
      paragraphs: [
        'Use the capacity type dropdown to filter dashboard data by capacity type. This affects all widgets and metrics on the page.',
      ],
      concepts: [
        { term: 'Platform', definition: 'Shared infrastructure capacities managed by the platform team.' },
        { term: 'Reference', definition: 'Template capacities used as baselines for client deployments.' },
        { term: 'ClientDedicated', definition: 'Capacities allocated exclusively to a single client.' },
        { term: 'MultiTenant', definition: 'Shared capacities serving multiple clients with resource isolation.' },
      ],
    },
    {
      title: 'Data Refresh',
      paragraphs: [
        'Dashboard data refreshes automatically every 30 seconds via polling. Stale data is cached for 10 seconds to reduce API calls. You can also manually refresh by navigating away and back.',
      ],
    },
    {
      title: 'Widget States',
      concepts: [
        { term: 'Loading', definition: 'A skeleton placeholder shows while data is being fetched from the API.' },
        { term: 'Error', definition: 'When an API call fails, a retry button appears. Click it to re-fetch the data.' },
        { term: 'Empty / Zero State', definition: 'When no data exists (e.g., no capacities configured), a guided message with setup links appears.' },
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Check the overall system health score at the top of the dashboard for a quick status summary.' },
        { step: 2, instruction: 'Review the Active Alerts widget to see if any critical or warning alerts need immediate attention.' },
        { step: 3, instruction: 'Use the Capacity Type dropdown to filter all dashboard widgets by a specific capacity type.' },
        { step: 4, instruction: 'If data appears stale, wait for the 30-second auto-refresh or navigate away and back to force a refresh.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Capacity Metrics', href: '/capacity-metrics' },
        { label: 'Backup Runs', href: '/backup' },
        { label: 'System Alerts', href: '/alerts' },
        { label: 'Workspaces', href: '/workspaces' },
      ],
    },
  ],
};

export default dashboardHelp;
